import { Map, Record } from 'immutable'
import { records } from 'data/v3Records'
import { resourceState, resourceReducers } from 'data/v3Resources'
import _ from 'lodash'

// We only want to deserialize fulfilled requests
const isFulfilled = type => /_FULFILLED$/.test(type)

const isDelete = type => /delete_FULFILLED$/.test(type)

/*
Listen for all fulfilled apiv3 actions, and place all deserialized records into their id -> Record
Map within their slice of state. This means if an experience comes in as an extra with id 5, it will
be available as state.apiv3.experiences.get(5) -> Experience

We also call all custom apiv3 reducers that are registered to handle any actions coming through.
*/
export const createReducer = (_records, _resourceState, _resourceReducers) => {
  /*
  Construct the basic apiv3 state. By default we store all records we see in a
  Map(id -> Record), which ends up in state.apiv3.xyz.id, where xyz is record.modelName
  */
  const recordStateShape = _records.reduce((stateShape, record) => {
    stateShape[record.modelName] = { id: Map() }
    return stateShape
  }, {})

  /*
  Merges custom state into the slice owned by a given record. These keys will be
  siblings of `id`, the id -> Record map.
  */
  _resourceState.forEach(([record, stateSlice]) => {
    recordStateShape[record.modelName] = { ...recordStateShape[record.modelName], ...stateSlice }
  })

  // Now we can build our initial state record definition
  const Apiv3State = Record(_.mapValues(recordStateShape, stateSliceShape => Record(stateSliceShape)()))

  return (state = Apiv3State(), action = {}) => {
    let newState = state
    if (action.meta && action.meta.apiv3source && isFulfilled(action.type)) {
      newState = newState.withMutations(m => {
        // handle record deletion
        if (isDelete(action.type)) {
          m.deleteIn([action.meta.apiv3source, 'id', action.meta.id])
        }

        // single record response
        _records.some(recordType => {
          const record = action.payload[recordType.modelName]
          if (record) {
            return m.setIn([recordType.modelName, 'id', record.id], record)
          } else {
            return false
          }
        })

        // extras
        action.payload.extras.forEach(x => {
          const camelCasedType = _.camelCase(x.type)
          if (m.has(camelCasedType)) {
            m.setIn([camelCasedType, 'id', x[x.type].id], x[x.type])
          }
        })

        // collection response
        if (action.payload.results) {
          action.payload.results.forEach(collectionResult => {
            m.setIn([action.meta.apiv3source, 'id', collectionResult.id], collectionResult)
          })
        }
      })
    }
    if (_resourceReducers[action.type]) {
      const modelName = action.meta.apiv3source
      newState = newState.set(modelName, _resourceReducers[action.type](newState.get(modelName), action))
    }
    return newState
  }
}

export const reducer = createReducer(records, resourceState, resourceReducers)
